import { useMfa } from '@iheartjane/menu';
import { EventNames, trackError } from '@iheartjane/tracking';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { useCheckout, useGoogleRecaptcha } from '@jane/shared-ecomm/providers';
import { track } from '@jane/shared-ecomm/tracking';
import { useJaneUser, userQueryKeys } from '@jane/shared/data-access';

import { useCheckoutPayments } from '../../CheckoutPayments/CheckoutPaymentsProvider';
import { useHeadlessKiosk } from '../../HeadlessKioskProvider';
import { useBuildCheckoutRequest } from './useBuildCheckoutRequest';
import { useHasNewPhone } from './useHasNewPhone';
import { useMaybeUpdateUser } from './useMaybeUpdateUser';
import type { PaymentOptions } from './usePaymentOptions';
import { PaymentMethods } from './usePaymentOptions';

export const useSubmitCheckout = () => {
  const maybeUpdateUser = useMaybeUpdateUser({ checkingOut: true });
  const { hasNewPhone, newPhone } = useHasNewPhone();
  const { getValue, reset, execute } = useGoogleRecaptcha();
  const { launch, requiresMfa } = useMfa();
  const { buildRequestValues } = useBuildCheckoutRequest();
  const { data: janeUser } = useJaneUser();
  const phoneNeedsVerification =
    !janeUser?.user.phone_verified_at && !janeUser?.user.external_user;

  const {
    customer: { authenticated },
    dispatches: { checkout, checkoutCancelled, checkoutError, checkoutStarted },
    store: { canpay_v2remotepay_enabled },
  } = useCheckout();
  const { isHeadlessKioskMode } = useHeadlessKiosk();
  const { selected } = useCheckoutPayments();

  const queryClient = useQueryClient();

  const getTrackingEvent = useCallback(() => {
    switch (selected) {
      case PaymentMethods.janePay:
        return EventNames.ClickedPayWithJanePay;
      case PaymentMethods.stronghold:
        return EventNames.ClickedPayWithStronghold;
      default:
        return null;
    }
  }, [selected]);

  // Submitting checkout operates as three function. When submitted, the first function
  // checks if recaptcha validation is required. Upon successful recaptcha (or if not required),
  // the next function is called that will check if MFA is required. Upon mfa success (or if not required),
  // the actual checkout submission function will be called.
  const handleCheckout = useCallback(() => {
    maybeUpdateUser();
    const recaptchaToken = getValue();
    reset();
    const requestValues = buildRequestValues();

    checkout({
      ...requestValues,
      // Skip recaptcha token in kiosk mode
      ...(isHeadlessKioskMode ? {} : { recaptcha_token: recaptchaToken }),
    });
  }, [buildRequestValues, checkout, isHeadlessKioskMode, reset]);

  const handleMfa = useCallback(() => {
    const shouldMfa =
      hasNewPhone ||
      (authenticated &&
        (phoneNeedsVerification ||
          (selected &&
            requiresMfa() &&
            (
              [
                PaymentMethods.janePay,
                PaymentMethods.stronghold,
              ] as PaymentOptions[]
            ).includes(selected))));

    if (shouldMfa) {
      launch({
        onCancel: () => checkoutCancelled(),
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: userQueryKeys.janeUser(),
          });
          handleCheckout();
        },
        newPhone: hasNewPhone ? newPhone : undefined,
      });
      return;
    } else {
      handleCheckout();
    }
  }, [
    authenticated,
    hasNewPhone,
    handleCheckout,
    launch,
    newPhone,
    phoneNeedsVerification,
    requiresMfa,
    selected,
  ]);

  const handleRecaptcha = useCallback(() => {
    checkoutStarted();
    const event = getTrackingEvent();
    event && track({ event });

    const skipRecaptcha =
      isHeadlessKioskMode ||
      (selected &&
        (
          [
            PaymentMethods.janePay,
            PaymentMethods.stronghold,
          ] as PaymentOptions[]
        ).includes(selected)) ||
      (selected === PaymentMethods.canpay && canpay_v2remotepay_enabled);

    const recaptchaToken = getValue();

    if (!skipRecaptcha && !recaptchaToken) {
      try {
        execute({
          onCancel: () => checkoutCancelled(),
          onError: () => {
            checkoutError('There was an error launching the ReCAPTCHA service');
            checkoutCancelled();
          },
          onSuccess: handleMfa,
        });
      } catch (e) {
        trackError('Google ReCAPTCHA error', { error: e });
        checkoutCancelled();
      }
    } else {
      handleMfa();
    }
  }, [
    canpay_v2remotepay_enabled,
    execute,
    getTrackingEvent,
    getValue,
    handleMfa,
    isHeadlessKioskMode,
    selected,
  ]);

  return {
    submit: handleRecaptcha,
  };
};
